import { z } from 'zod';
import { autoStatusFlagsSchema, workingHoursSchema } from './presence-status.js';
import { colorSchemeSchema, themeModeSchema } from './themes.js';
import { appIconSchema } from './app-icons.js';
export const roleSchema = z.enum(['owner', 'admin', 'member', 'guest']);
export const presenceSchema = z.enum(['active', 'away', 'dnd', 'offline']);
/**
 * Felhasznalonev.
 *
 * A megkotesek a felismerhetoseget vedik: az `@emlites` es a belepesi mezo is
 * ezt hasznalja, tehat egyertelmunek kell lennie. Kisbetu, szam, pont,
 * kotojel, alulvonas - ekezet nincs, mert egy "kis.éva" nevet a kollegak fele
 * ekezet nelkul gepelne be, es nem ertene, miert nem megy.
 */
export const USERNAME_RE = /^[a-z0-9][a-z0-9._-]{1,30}$/;
export const usernameSchema = z
    .string()
    .trim()
    .toLowerCase()
    .regex(USERNAME_RE, 'Csak kisbetű, szám, pont, kötőjel és aluvonás - 2-31 karakter.');
/** Neveket vagy beceneveket lat-e a felhasznalo a feluleten. */
export const nameDisplayModes = ['nev', 'becenev'];
export const nameDisplayModeSchema = z.enum(nameDisplayModes);
/**
 * Alapbol a BECENEVEKET latjuk.
 *
 * Egy belso csapatchatben ugyis a beceneveket hasznaljak; akinek pedig nincs,
 * annal a rendes neve jelenik meg. Igy ez a valasztas senkinek nem hianyzik
 * semmit, es barki atallithatja a profiljaban.
 */
export const DEFAULT_NAME_DISPLAY = 'becenev';
/** A felhasznalo publikus kepe - ez megy ki a klienseknek. */
export const publicUserSchema = z.object({
    id: z.string(),
    email: z.string(),
    /** A masik belepesi azonosito. `null` a botoknal es a regi fiokoknal. */
    username: z.string().nullable(),
    displayName: z.string(),
    /** Amit a kollegak egymas kozt hasznalnak. Csak akkor latszik, ha a NEZO keri. */
    nickname: z.string().nullable(),
    realName: z.string().nullable(),
    title: z.string().nullable(),
    /** Nevjegyzek (AD2): osztaly, telefonszam, mellek. */
    department: z.string().nullable().default(null),
    phone: z.string().nullable().default(null),
    extension: z.string().nullable().default(null),
    avatarUrl: z.string().nullable(),
    timezone: z.string(),
    /**
     * A csapat, ahol a felhasznalo van otthon.
     *
     * A kliensnek azert kell, hogy tudja, kit mutat a sajat csapatabol, es kit
     * egy megosztott csatornan keresztul - a ketto nem ugyanaz a viszony.
     */
    teamId: z.string(),
    /** A csapaton BELUL ertendo szerep. */
    role: roleSchema,
    /** Mindent lat es szerkeszt, minden csapatban. */
    isSuperadmin: z.boolean(),
    statusEmoji: z.string().nullable(),
    statusText: z.string().nullable(),
    statusExpiresAt: z.string().nullable(),
    /**
     * Fokusz mod vege, es a munkaido.
     *
     * NYILVANOS adat: ebbol szamolja ki a kliens az automatikus statuszt
     * ("munkaidon kivul"), es pont az a lenyege, hogy a tobbiek lassak.
     */
    focusUntil: z.string().nullable(),
    /**
     * Az aktualis vagy kovetkezo FOGLALT sav az Outlook-naptarbol (docs/23) - cim
     * nelkul. Ebbol lesz a "Megbeszelesen" statusz; `null`, ha nincs Outlook-
     * kapcsolat vagy nincs kozeli megbeszeles.
     */
    meeting: z.object({ from: z.string(), until: z.string() }).nullable().default(null),
    workingHours: workingHoursSchema,
    autoStatus: autoStatusFlagsSchema,
    /** Megjeleno tema - a felhasznalohoz kotve, hogy kovesse a gepek kozott. */
    themeMode: themeModeSchema,
    colorScheme: colorSchemeSchema,
    /**
     * A valasztott alkalmazas-ikon.
     *
     * SAJAT dontes, a tobbieket nem erinti - de ugyanugy a felhasznalohoz
     * kotve, mint a tema, hogy kovesse a gepek kozott.
     */
    appIcon: appIconSchema,
    /**
     * A SAJAT dontesem arrol, hogyan latom a tobbieket.
     *
     * Publikus mezo, de csak a sajat rekordunkon van jelentosege - a tobbiek
     * beallitasa minket nem erint.
     */
    nameDisplay: nameDisplayModeSchema,
    isActive: z.boolean(),
    isBot: z.boolean(),
    /**
     * Telefonalhat-e kifele (a huddle-bol). Admin kapcsolja; a szerver minden
     * hivasnal ujra ellenorzi - ez csak a felulet gombjanak szol.
     */
    canCallOut: z.boolean().default(false),
});
export const isAdmin = (user) => user.role === 'admin' || user.role === 'owner';
/**
 * A megjelenitendo nev - a NEZO beallitasa szerint.
 *
 * Ha valakinek nincs beceneve, a rendes neve jelenik meg: egy ures nev sehol
 * nem elfogadhato, es a "nevtelen kollega" rosszabb, mint egy be nem tartott
 * preferencia.
 */
export function resolveDisplayName(user, mode) {
    if (mode === 'becenev')
        return user.nickname?.trim() || user.displayName;
    return user.displayName;
}
/**
 * A nevjegyzek mezoi - a sajat profilban es az admin szerkesztojeben is.
 *
 * A telefonszamot laza szaballyal fogadjuk: szamjegyek, szokoz, +, -, /,
 * zarojel. Egy kotelezo formatum ("+36 ...") a regi, kezzel irt szamoknal
 * csak akadalyt jelentene.
 */
export const PHONE_TEXT_RE = /^[+\d][\d\s/()-]{2,39}$/;
export const directoryFieldsInput = z.object({
    title: z.string().trim().max(100).nullable().optional(),
    department: z.string().trim().max(100).nullable().optional(),
    phone: z
        .string()
        .trim()
        .regex(PHONE_TEXT_RE, 'Érvénytelen telefonszám.')
        .nullable()
        .optional()
        .or(z.literal('').transform(() => null)),
    extension: z
        .string()
        .trim()
        .regex(/^[\d*#]{1,10}$/, 'A mellék csak számjegy lehet.')
        .nullable()
        .optional()
        .or(z.literal('').transform(() => null)),
});
/** Felhasznalonev-javaslat az e-mail cimbol - a meghivo urlap tolti ki elore. */
export function suggestUsername(email) {
    const local = email.split('@')[0] ?? '';
    return local
        .toLowerCase()
        .normalize('NFD')
        .replace(/\p{Diacritic}/gu, '')
        .replace(/[^a-z0-9._-]/g, '')
        .slice(0, 31);
}
//# sourceMappingURL=user.js.map